import { useEffect, useRef } from 'react';

export type UseEffectIfWindowIsFocusedArgs = {
  effectIfFocused?: () => void;
  effectIfBlurred?: () => void;
  runIf?: boolean;
};

export const useEffectIfWindowIsFocused = ({
  effectIfFocused = () => {},
  effectIfBlurred = () => {},
  runIf = true,
}: UseEffectIfWindowIsFocusedArgs) => {
  const timerRef = useRef(0);

  const windowFocusedRef = useRef(true);

  useEffect(() => {
    if (!runIf) {
      return () => {};
    }

    const focusCallback = () => {
      windowFocusedRef.current = true;
    };
    const blurCallback = () => {
      windowFocusedRef.current = false;
    };

    window.addEventListener('focus', focusCallback);
    window.addEventListener('blur', blurCallback);

    return () => {
      window.removeEventListener('focus', focusCallback);
      window.removeEventListener('blur', blurCallback);
    };
  }, [runIf]);

  const run = () => {
    // Setting timeout to allow the window event callbacks to run
    timerRef.current = window.setTimeout(() => {
      if (windowFocusedRef.current) {
        effectIfFocused();
      } else {
        effectIfBlurred();
      }
    }, 0);
  };

  const cancel = () => {
    window.clearTimeout(timerRef.current);
  };

  return { cancel, run };
};
